import Sentry from "@similarweb/sentry-logger";

import type { ThunkActionCommon } from "single-spa/store/types";

import type { Integration } from "../../management/types";
import contactToContactIdCompanyPair from "../helpers/contactToContactIdCompanyPair";
import trackingService from "../services/tracking/trackingService";
import type { ContactSearchEntry, ContactSearchEntryWithKey, CRMEntity } from "../types/common";

import {
    initExportToCrm,
    resetExportToCrm,
    setExportFlowOrigin,
    setExportFlowPhase,
    setRequestedContactsForExport,
    tableRowsSelectionChangedAction,
} from "./action-creators";
import { extractContactsPreview, isFailedFetchingPreviewAction } from "./action-utils";
import {
    exportContactsToCrmAsContactsThunk,
    exportContactsToCrmAsLeadsThunk,
    fetchExportToCrmPreviewThunk,
} from "./effects";
import { resetExportFlowStateThunk } from "./export-flow-thunks";
import { selectCrmExportPairs, selectCrmToExportContacts } from "./selectors";

export function startRowCrmContactExportThunk(countries: readonly number[]): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);
        const pairs = selectCrmExportPairs(state);

        if (!integration) {
            dispatch(resetExportFlowStateThunk());
            return;
        }

        dispatch(setExportFlowPhase({ type: "matchingCrmContacts", variant: "crm", integration }));

        try {
            const resultAction = await dispatch(fetchExportToCrmPreviewThunk(pairs, integration));

            if (isFailedFetchingPreviewAction(resultAction)) {
                dispatch(resetExportFlowStateThunk());
                return;
            }

            const contactsPreview = extractContactsPreview(resultAction);
            const shouldAutoExport =
                contactsPreview.length > 0 &&
                contactsPreview.every((contact) => contact.matchedCompanies.length === 1 && !contact.isExistAsLead);

            if (shouldAutoExport) {
                dispatch(setExportFlowPhase({ type: "exportingCrmContacts", variant: "crm", integration }));
                try {
                    const result = await dispatch(exportContactsToCrmAsContactsThunk(countries));
                    trackingService.trackContactsExportToCrm(integration, result.contacts, result.exportedCount);
                    dispatch(resetExportToCrm());
                } finally {
                    dispatch(resetExportFlowStateThunk());
                }
            } else {
                dispatch(setExportFlowPhase({ type: "previewingCrmContacts", variant: "crm", integration }));
            }
        } catch (error) {
            Sentry.errorTracker({ message: "startRowCrmContactExportThunk error", e: error });
            dispatch(resetExportFlowStateThunk());
        }
    };
}

export function confirmRowCrmPreviewExportThunk(countries: readonly number[]): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);

        if (!integration) {
            dispatch(resetExportFlowStateThunk());
            return;
        }

        dispatch(setExportFlowPhase({ type: "exportingCrmContacts", variant: "crm", integration }));

        try {
            const result = await dispatch(exportContactsToCrmAsContactsThunk(countries));
            trackingService.trackContactsExportToCrm(integration, result.contacts, result.exportedCount);
            dispatch(resetExportToCrm());
        } finally {
            dispatch(resetExportFlowStateThunk());
        }
    };
}

export function singleContactCrmExportThunk({
    contact,
    integration,
    exportAs,
    countries,
}: {
    contact: ContactSearchEntry;
    integration: Integration;
    exportAs: CRMEntity;
    countries: readonly number[];
}): ThunkActionCommon<Promise<void>> {
    return async (dispatch) => {
        dispatch(setExportFlowOrigin("row"));
        dispatch(tableRowsSelectionChangedAction([contact as ContactSearchEntryWithKey]));
        dispatch(setRequestedContactsForExport([contact]));
        dispatch(initExportToCrm({ crm: integration, as: exportAs }));

        const pairs = [contactToContactIdCompanyPair(contact)];

        if (exportAs !== "contact") {
            try {
                const result = await dispatch(exportContactsToCrmAsLeadsThunk(pairs, countries));
                trackingService.trackContactsExportToCrm(integration, result.contacts, result.exportedCount);
                dispatch(resetExportToCrm());
            } finally {
                dispatch(resetExportFlowStateThunk());
            }
            return;
        }

        await dispatch(startRowCrmContactExportThunk(countries));
    };
}
